<template>
  <Listbox
    as="div"
    class="z-backdrop-reset relative"
    :def="props.active"
    @update:modelValue="handleLanguageSelected"
  >
    <ListboxButton
      @click="showMenu = !showMenu"
      class="relative cursor-pointer text-sm font-semibold rounded-sm py-1.5 pl-3 pr-9 text-left focus:outline-none focus:ring-1 hover:ring-1 z-backdrop-content"
      :class="
        props.inverted
          ? 'text-white focus:border-white focus:ring-white hover:ring-white'
          : 'text-gray-600 focus:border-gray-600 focus:ring-gray-600 hover:ring-gray-300'
      "
    >
      <span class="block truncate">{{ language }}</span>
      <span
        class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
      >
        <ChevronDownIcon
          class="h-5 w-5"
          :class="props.inverted ? 'text-white' : 'text-gray-600'"
          aria-hidden="true"
        />
      </span>
    </ListboxButton>

    <div v-if="showMenu">
      <Backdrop @backdropClicked="showMenu = false" />
      <transition
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <ListboxOptions
          static
          class="absolute bottom-full z-10 mb-1 max-h-60 w-auto overflow-auto rounded-sm bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-backdrop-content"
        >
          <ListboxOption
            as="template"
            v-for="language in languages"
            :key="language"
            :value="language"
            v-slot="{ active, selected }"
          >
            <li
              :class="[
                active ? 'text-gray-900' : 'text-gray-900',
                'relative cursor-pointer select-none py-2 pl-3 pr-9 hover:bg-gray-100',
              ]"
            >
              <span
                :class="[
                  selected ? 'font-normal' : 'font-normal',
                  'block truncate',
                ]"
                >{{ language.label }}</span
              >
            </li>
          </ListboxOption>
        </ListboxOptions>
      </transition>
    </div>
  </Listbox>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref } from "vue";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/vue";
import { ChevronDownIcon } from "@heroicons/vue/20/solid";
import { useI18n } from "vue-i18n";
import { usePage } from "@inertiajs/vue3";
import Backdrop from "@/Tailwind/Components/Backdrop.vue";

const props = defineProps({
  active: String,
  inverted: {
    type: Boolean,
    default: false,
  },
});

const { t, messages } = useI18n();
const all = usePage().props.tenant.settings.allowed_messenger_locales;
const allowedLocales = Object.keys(all).filter((lang) => all[lang] === true);

const showMenu = ref(false);

const languages = computed(() => {
  return Object.keys(messages.value)
    .map((lang) => ({
      label: t(`validation.values.locale.${lang}`),
      locale: lang,
    }))
    .filter((lang) => allowedLocales.includes(lang.locale));
});

const language = computed(() => {
  return t(`validation.values.locale.${props.active}`);
});

const emit = defineEmits(["language-selected"]);

const handleLanguageSelected = (language) => {
  showMenu.value = false;
  emit("language-selected", language.locale);
};
</script>
